import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as adminService from "@/shared/api/services/adminService";

import { generateApiKey, generateSecretKey } from "@/shared/utils/utilities";

import GeneralTab from "./merchant-form-tabs/GeneralTab";
import ApiTab from "./merchant-form-tabs/ApiTab";
import LimitsTab from "./merchant-form-tabs/LimitsTab";

export default function MerchantForm({
  merchant,
  open,
  onClose,
  onSave,
  engines = [],
  resellers = [],
}) {
  const { t } = useLanguage();
  const [currentTab, setCurrentTab] = useState("general");
  const [saving, setSaving] = useState(false);
  const [keysRegenerated, setKeysRegenerated] = useState(false);

  const methods = useForm({
    defaultValues: {
      email: "",
      password: "",
      name: "",
      status: "ACTIVE",
      parent_type: "direct",
      engine_id: "",
      reseller_id: "",
      code: "",
      currency: "THB",
      minDeposit: 100,
      maxDeposit: 1000000,
      minWithdrawal: 100,
      maxWithdrawal: 500000,
      dailyDepositLimit: 10000000,
      dailyWithdrawalLimit: 5000000,
      depositEnabled: true,
      withdrawalEnabled: true,
      fee_type: "PERCENTAGE",
      fee_rate: 2.5,
      notes: "",
      apiKey: "",
      secretKey: "",
      callback: "",
      webhook: "",
    },
    mode: "onTouched",
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (open) {
      setCurrentTab("general");
      if (!merchant) {
        reset({
          email: "",
          password: "",
          name: "",
          status: "ACTIVE",
          parent_type: "direct",
          engine_id: "",
          reseller_id: "",
          code: "",
          currency: "THB",
          minDeposit: 100,
          maxDeposit: 1000000,
          minWithdrawal: 100,
          maxWithdrawal: 500000,
          dailyDepositLimit: 10000000,
          dailyWithdrawalLimit: 5000000,
          depositEnabled: true,
          withdrawalEnabled: true,
          fee_type: "PERCENTAGE",
          fee_rate: 2.5,
          notes: "",
          apiKey: generateApiKey(),
          secretKey: generateSecretKey(),
          callback: "",
          webhook: "",
        });
        setKeysRegenerated(false);
      } else {
        reset({
          email: merchant.email || "",
          password: "",
          name: merchant.name || "",
          status: merchant.status || "ACTIVE",
          parent_type: merchant.createdByEngineId
            ? "engine"
            : merchant.createdByResellerId
              ? "reseller"
              : "direct",
          engine_id: merchant.createdByEngineId || "",
          reseller_id: merchant.createdByResellerId || "",
          code: merchant.code || "",
          currency: merchant.currency || "THB",
          minDeposit: merchant.minDeposit ?? 100,
          maxDeposit: merchant.maxDeposit ?? 1000000,
          minWithdrawal: merchant.minWithdrawal ?? 100,
          maxWithdrawal: merchant.maxWithdrawal ?? 500000,
          dailyDepositLimit: merchant.dailyDepositLimit ?? 10000000,
          dailyWithdrawalLimit: merchant.dailyWithdrawalLimit ?? 5000000,
          depositEnabled: merchant.depositEnabled !== false,
          withdrawalEnabled: merchant.withdrawalEnabled !== false,
          fee_type: merchant.feeType || "PERCENTAGE",
          fee_rate: merchant.feeRate ?? 2.5,
          notes: merchant.notes || "",
          apiKey: merchant.apiKey || "",
          secretKey: merchant.secretKey ? "••••••••" : "",
          callback: merchant.callbackUrl || "",
          webhook: merchant.webhookUrl || "",
        });
        setKeysRegenerated(false);
      }
    }
  }, [open, merchant, reset]);

  const onFormSubmit = async (data) => {
    setSaving(true);
    try {
      let dataToSave;
      if (merchant) {
        dataToSave = {
          name: data.name,
          code: data.code,
          status: data.status,
          currency: data.currency,
          minDeposit: Number(data.minDeposit) || 0,
          maxDeposit: Number(data.maxDeposit) || 0,
          minWithdrawal: Number(data.minWithdrawal) || 0,
          maxWithdrawal: Number(data.maxWithdrawal) || 0,
          dailyDepositLimit: Number(data.dailyDepositLimit) || 0,
          dailyWithdrawalLimit: Number(data.dailyWithdrawalLimit) || 0,
          depositEnabled: data.depositEnabled ? "true" : "false",
          withdrawalEnabled: data.withdrawalEnabled ? "true" : "false",
          fee_type: data.fee_type,
          fee_rate: Number(data.fee_rate) || 0,
          notes: data.notes || null,
          callback: data.callback || null,
          webhook: data.webhook || null,
        };
        if (keysRegenerated) {
          dataToSave.apiKey = data.apiKey;
          dataToSave.secretKey = data.secretKey;
        }

        if (data.password && data.password.trim() !== "") {
          await adminService.changeMerchantPassword(merchant.id, data.password);
        }
      } else {
        dataToSave = {
          email: data.email,
          password: data.password,
          name: data.name,
          code: data.code,
          currency: data.currency,
          minDeposit: Number(data.minDeposit) || 0,
          maxDeposit: Number(data.maxDeposit) || 0,
          minWithdrawal: Number(data.minWithdrawal) || 0,
          maxWithdrawal: Number(data.maxWithdrawal) || 0,
          dailyDepositLimit: Number(data.dailyDepositLimit) || 0,
          dailyWithdrawalLimit: Number(data.dailyWithdrawalLimit) || 0,
          depositEnabled: data.depositEnabled ? "true" : "false",
          withdrawalEnabled: data.withdrawalEnabled ? "true" : "false",
          fee_type: data.fee_type,
          fee_rate: Number(data.fee_rate) || 0,
          notes: data.notes || undefined,
          apiKey: data.apiKey,
          secretKey: data.secretKey,
          callback: data.callback || undefined,
          webhook: data.webhook || undefined,
        };

        if (data.parent_type === "engine" && data.engine_id) {
          dataToSave.engineId = data.engine_id;
        } else if (data.parent_type === "reseller" && data.reseller_id) {
          dataToSave.resellerId = data.reseller_id;
        }
      }

      await onSave(dataToSave);
    } catch (error) {
      console.error("Failed to save merchant:", error);
      toast.error(t("saveFailed") || "Failed to save merchant");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = (e, nextTab) => {
    e.preventDefault();
    setCurrentTab(nextTab);
  };

  const tabOrder = ["general", "api", "limits"];
  const currentTabIndex = tabOrder.indexOf(currentTab);
  const isLastTab = currentTabIndex === tabOrder.length - 1;
  const isFirstTab = currentTabIndex === 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {merchant ? t("editMerchant") : t("addNewMerchant")}
          </DialogTitle>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
            <Tabs
              value={currentTab}
              onValueChange={setCurrentTab}
              className="mt-4"
            >
              <TabsList className="grid grid-cols-3 w-full bg-slate-100 p-1 rounded-xl">
                <TabsTrigger
                  value="general"
                  className="rounded-lg transition-all duration-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  {t("general")}
                </TabsTrigger>
                <TabsTrigger
                  value="api"
                  className="rounded-lg transition-all duration-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  {t("apiSettings")}
                </TabsTrigger>
                <TabsTrigger
                  value="limits"
                  className="rounded-lg transition-all duration-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  {t("limitsFees")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="general">
                <GeneralTab
                  merchant={merchant}
                  t={t}
                  engines={engines}
                  resellers={resellers}
                />
              </TabsContent>
              <TabsContent value="api">
                <ApiTab
                  merchant={merchant}
                  t={t}
                  onKeysRegenerated={() => setKeysRegenerated(true)}
                />
              </TabsContent>
              <TabsContent value="limits">
                <LimitsTab merchant={merchant} t={t} />
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6 flex justify-between">
              <div className="flex gap-2">
                <Button
                  key="cancel-btn"
                  variant="outline"
                  type="button"
                  onClick={onClose}
                >
                  {t("cancel")}
                </Button>
                {!isFirstTab && (
                  <Button
                    key="back-btn"
                    variant="outline"
                    type="button"
                    onClick={(e) => handleNext(e, tabOrder[currentTabIndex - 1])}
                  >
                    {t("backButton")}
                  </Button>
                )}
              </div>
              <div>
                {!isLastTab ? (
                  <Button
                    key="next-btn"
                    type="button"
                    onClick={(e) => handleNext(e, tabOrder[currentTabIndex + 1])}
                    className="hover:bg-blue-700 hover:scale-105 transition-all duration-300 shadow-sm hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-700"
                  >
                    {t("nextButton")}
                  </Button>
                ) : (
                  <Button
                    key="submit-btn"
                    type="submit"
                    disabled={saving}
                    className="hover:bg-blue-700 hover:scale-105 transition-all duration-300 shadow-sm hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-700"
                  >
                    {saving
                      ? t("saving")
                      : merchant
                        ? t("updateMerchant")
                        : t("createMerchantBtn")}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
